import { Alert, alertClasses } from "@mui/material";
import { styled } from "@mui/system";
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import WarningIconOutLine from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';


/**
 * A styled component that customizes the appearance of an Alert component.
 * 
 * The `ScriptMessage` component applies different styles based on the alert's color type.
 * It supports the following alert color types:
 * - Success: Green background with green text and a specific box shadow.
 * - Info: Neutral background with primary text and a specific box shadow.
 * - Warning: Light orange background with dark orange text and a specific box shadow.
 * - Error: Red background with dark red text and a specific box shadow.
 * 
 * Additionally, it customizes the title and message of the alert:
 * - The title (`.MuiAlertTitle-root`) is bold and uses the warning dark color from the theme.
 * - The error message (`.MuiError-message`) is displayed as a flex container with centered items.
 * 
 * @component
 * @example
 * return (
 *   <ScriptMessage severity="success">
 *     This is a success message!
 *   </ScriptMessage>
 * );
 */
export const ScriptMessage = styled(Alert, { target: 'ScriptMessage' })`
    border-radius: ${({ theme }) => theme.spacing(1)};
    font-size: 14px;
    margin-bottom: ${({ theme }) => theme.spacing(2)};
    &.${alertClasses.colorSuccess} {
        background-color: ${({ theme }) => theme.vars.palette.green[100]};
        color: ${({ theme }) => theme.vars.palette.green.main};
        box-shadow: 0px 6px 12px 0px #1721971F;
    }
    &.MuiAlert-colorInformation {
        background-color: ${({ theme }) => theme.palette.neutrals.neutralButtonsBg};
        color: ${({ theme }) => theme.vars.palette.primary.main};
        box-shadow: 0px 6px 12px 0px #1721971F;
    }
    &.${alertClasses.colorWarning} {
        background-color: #FFE6C8;
        color: #A63708;
        box-shadow: 0px 6px 12px 0px #1721971F;
    }
    &.${alertClasses.colorError} {
        background-color: ${({ theme }) => theme.vars.palette.red[100]};
        color: ${({ theme }) => theme.vars.palette.red[900]};
        box-shadow: 0px 6px 12px 0px #9C244F1F;
    }
    
    .MuiAlertTitle-root {
        font-weight: bold;
        color: ${({ theme }) => theme.palette.warning.dark};
    }
    .MuiError-message {
        display: flex;
        align-items: center;
    }
`;


export const CheckCircleIconSuccess = styled(CheckCircleIcon)`
    color: ${({ theme }) => theme.vars.palette.green[700]};
`;
export const WarningIconStyle = styled(WarningIconOutLine)`
    color: ${({ theme }) => theme.vars.palette.warning[800]};
`;
export const InformationIcon = styled(InfoOutlinedIcon)`
    color: ${({ theme }) => theme.vars.palette.primary.main};
`;
export const ErrorIcon = styled(ErrorOutlineIcon)`
    color: ${({ theme }) => theme.vars.palette.red[700]};
`;